import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { HiOutlineClipboardCopy } from "react-icons/hi";

import { setUser } from "../../redux/auth/actions";
import Loader from "../../components/loader";
import LoadingButton from "../../components/loadingButton";
import api from "../../services/api";
import FileInput from "../../components/FileInput";
import { toDateInputValue } from "../../utils";

export default function Account() {
  const user = useSelector((state) => state.Auth.user);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [values, setValues] = useState({ ...user, birthdate: toDateInputValue(user?.birthdate) });

  if (!user) return <Loader />;

  async function handleSubmit(e) {
    e.preventDefault();
    setIsLoading(true);
    try {
      const { data, code } = await api.put(`/user/${user._id}`, values);
      if (code) throw code;
      dispatch(setUser(data));
      toast.success("Updated!");
    } catch (e) {
      toast.error("Error: " + e);
    }
    setIsLoading(false);
  }

  return (
    <div className="w-full min-h-[90vh] bg-gray-100 p-4">
      <div className="max-w-3xl mx-auto space-y-4">
        {/* Header */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 flex items-center gap-4">
          <FileInput
            value={user.avatar}
            onChange={async (e) => {
              const avatarValue = Array.isArray(e.target.value) ? e.target.value[0] : e.target.value;
              const { data } = await api.put(`/user/${user._id}`, { avatar: avatarValue });
              dispatch(setUser(data));
              toast.success("Updated!");
            }}
            folder="/user"
          />
          <div>
            <div className="text-xl font-semibold">{user.name}</div>
            <div className="text-sm text-gray-500">{user.email}</div>
          </div>
        </div>

        {/* Personal info */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
          <h2 className="text-base font-semibold text-gray-800">Personal information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Name" value={values.name} onChange={(v) => setValues({ ...values, name: v })} />
            <Field label="Email" value={values.email} onChange={(v) => setValues({ ...values, email: v })} />
            <Field label="Birthdate" type="date" value={values.birthdate} onChange={(v) => setValues({ ...values, birthdate: v })} />
            <Field label="Github" value={values.github} onChange={(v) => setValues({ ...values, github: v })} placeholder="https://github.com/..." />
            <Field label="LinkedIn" value={values.linkedin} onChange={(v) => setValues({ ...values, linkedin: v })} placeholder="https://linkedin.com/in/..." />
            <Field label="Calendly" value={values.calendly} onChange={(v) => setValues({ ...values, calendly: v })} />
            <Field label="Meeting" value={values.meeting} onChange={(v) => setValues({ ...values, meeting: v })} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              rows={3}
              className="input w-full"
              value={values.description || ""}
              onChange={(e) => setValues({ ...values, description: e.target.value })}
            />
          </div>
          {(values.role === "client" || values.role === "lead") && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={values.newsletter} onChange={(e) => setValues({ ...values, newsletter: e.target.checked })} className="rounded" />
              Newsletter
            </label>
          )}
          <div className="flex justify-end">
            <LoadingButton className="bg-blue-600 hover:bg-blue-700 text-white text-sm py-2 px-4 rounded-md" loading={isLoading} onClick={handleSubmit}>
              Save changes
            </LoadingButton>
          </div>
        </form>

        {/* API Key */}
        <ApiKeySection />
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input className="input w-full" type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

function ApiKeySection() {
  const user = useSelector((state) => state.Auth.user);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [showKey, setShowKey] = useState(false);

  async function generateKey() {
    setLoading(true);
    try {
      const key = "sk_" + Array.from(crypto.getRandomValues(new Uint8Array(32)), (b) => b.toString(16).padStart(2, "0")).join("");
      const { data, ok } = await api.put(`/user/${user._id}`, { api_key: key });
      if (!ok) throw new Error();
      dispatch(setUser(data));
      setShowKey(true);
      toast.success("API key generated!");
    } catch (e) {
      toast.error("Error generating API key");
    }
    setLoading(false);
  }

  async function revokeKey() {
    if (!window.confirm("Revoke your API key? Any tool using it will stop working.")) return;
    try {
      const { data, ok } = await api.put(`/user/${user._id}`, { api_key: "" });
      if (!ok) throw new Error();
      dispatch(setUser(data));
      setShowKey(false);
      toast.success("API key revoked");
    } catch (e) {
      toast.error("Error revoking API key");
    }
  }

  function copy(text) {
    navigator.clipboard.writeText(text);
    toast.success("Copied!");
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
      <div>
        <h2 className="text-base font-semibold text-gray-800">API Key</h2>
        <p className="text-sm text-gray-500">Connect external tools like Claude Code to your account.</p>
      </div>

      {user.api_key ? (
        <div>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-gray-50 px-3 py-2 rounded border border-gray-200 text-sm font-mono break-all">
              {showKey ? user.api_key : user.api_key.slice(0, 8) + "..." + user.api_key.slice(-4)}
            </code>
            <button onClick={() => setShowKey(!showKey)} className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">
              {showKey ? "Hide" : "Show"}
            </button>
            <button onClick={() => copy(user.api_key)} className="p-2 hover:bg-gray-100 rounded" title="Copy">
              <HiOutlineClipboardCopy className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <div className="flex gap-3 mt-2">
            <button onClick={generateKey} disabled={loading} className="text-sm text-blue-600 hover:underline disabled:opacity-50">
              Regenerate
            </button>
            <button onClick={revokeKey} className="text-sm text-red-600 hover:underline">
              Revoke
            </button>
          </div>
        </div>
      ) : (
        <button onClick={generateKey} disabled={loading} className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700 disabled:opacity-50">
          Generate API key
        </button>
      )}

      <details className="text-sm">
        <summary className="cursor-pointer text-gray-500 hover:text-gray-700">Setup instructions</summary>
        <div className="mt-2 p-3 bg-gray-50 rounded-md">
          <p className="text-xs text-gray-500 mb-2">
            Add this to your <code className="bg-gray-200 px-1 rounded">.mcp.json</code>:
          </p>
          <pre className="bg-gray-800 text-green-400 text-xs p-3 rounded-md overflow-x-auto">
            {JSON.stringify(
              { mcpServers: { cowork: { type: "url", url: "https://api-accounting.selego.co/mcp", headers: { "x-api-key": user.api_key || "<your-api-key>" } } } },
              null,
              2,
            )}
          </pre>
        </div>
      </details>
    </div>
  );
}
